/**
 * Phase 261j — Push notifications control surface.
 *
 * Sits inside Profile → Security alongside `PwaInstallCard`. Lets every
 * authenticated user enable/disable native lockscreen alerts + app-icon
 * badging.  Honest UX: surface the actual browser permission state, never
 * pretend a denied permission can be flipped without browser intervention.
 */
import { useState } from 'react';
import {
  BellRing, BellOff, BellPlus, ShieldAlert, AlertTriangle, Loader2,
  Send, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { usePwaNotifications } from '@/hooks/usePwaNotifications';
import PushPreferencesPanel from '@/components/PushPreferencesPanel';
import { toast } from 'sonner';

export default function PushNotificationsCard() {
  const { user } = useAuth();
  const { supported, permission, isSubscribed, status, enable, disable, testSelf } =
    usePwaNotifications(user);
  const [lastResult, setLastResult] = useState(null);

  if (!supported) {
    return (
      <Card data-testid="push-notifications-card" data-state="unsupported" className="border-l-4 border-l-slate-400">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BellOff className="h-5 w-5 text-slate-500" />
            Push notifications unavailable
          </CardTitle>
          <CardDescription>
            This browser does not support Web Push.  Try Chrome / Edge / Brave
            on desktop or Android, or install Out-Play to your iPhone home
            screen (push lands when iOS finishes shipping support).
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Permission denied — browser-level UI is the only way back.
  if (permission === 'denied') {
    return (
      <Card data-testid="push-notifications-card" data-state="denied" className="border-l-4 border-l-rose-500">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-rose-600" />
            Notifications blocked
          </CardTitle>
          <CardDescription>
            You previously blocked notifications for Out-Play.  Unblock them in
            your browser's site settings (click the lock icon in the address
            bar → "Notifications: Allow"), then reload this page.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleEnable = async () => {
    const res = await enable();
    setLastResult(res);
    if (res.ok) {
      toast.success('Push notifications enabled on this device.');
    } else if (res.reason === 'denied') {
      toast.error('Notifications denied — you can re-enable from your browser settings.');
    } else if (res.reason === 'vapid_not_configured') {
      toast.error('Server-side push is not configured. Please contact support.');
    } else {
      toast.error('Could not enable push notifications. Check console for details.');
    }
  };

  const handleDisable = async () => {
    const res = await disable();
    setLastResult(res);
    if (res.ok) {
      toast.success('Push notifications disabled on this device.');
    } else {
      toast.error('Could not disable push notifications.');
    }
  };

  const handleTest = async () => {
    const res = await testSelf();
    setLastResult(res);
    if (res.ok && res.payload?.sent > 0) {
      toast.success('Test push fired — check your lockscreen / notification tray.');
    } else if (res.ok && res.payload?.status === 'no_subscription') {
      toast.info('No active subscription on this device — enable push first.');
    } else if (res.ok && res.payload?.sent === 0) {
      toast.warning('Push dispatcher ran but no devices were reachable.');
    } else {
      toast.error(`Test push failed: ${res.reason || 'unknown error'}`);
    }
  };

  const enabling = status === 'enabling';
  const disabling = status === 'disabling';
  const testing = status === 'testing';
  const anyInflight = enabling || disabling || testing;

  return (
    <Card data-testid="push-notifications-card" data-state={isSubscribed ? 'enabled' : 'eligible'} className={`border-l-4 ${isSubscribed ? 'border-l-emerald-500' : 'border-l-sky-500'}`}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {isSubscribed ? (
            <BellRing className="h-5 w-5 text-emerald-600" />
          ) : (
            <BellPlus className="h-5 w-5 text-sky-600" />
          )}
          {isSubscribed ? 'Push notifications are on' : 'Enable push notifications'}
        </CardTitle>
        <CardDescription>
          {isSubscribed
            ? 'You\'ll get lockscreen alerts for new messages, fee reminders, attendance and event updates.  Out-Play\'s app icon will badge with your unread count.'
            : 'Get instant lockscreen pings + a badge on the Out-Play app icon when you have unread updates.  Works on installed PWA and supported desktop browsers.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {!isSubscribed ? (
            <Button
              onClick={handleEnable}
              disabled={anyInflight}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="push-notifications-enable-btn"
            >
              {enabling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BellPlus className="h-4 w-4 mr-1.5" />}
              {enabling ? 'Enabling…' : 'Turn on push alerts'}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={anyInflight}
                data-testid="push-notifications-test-btn"
              >
                {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                {testing ? 'Sending…' : 'Send a test push'}
              </Button>
              <Button
                variant="ghost"
                onClick={handleDisable}
                disabled={anyInflight}
                className="text-slate-600 hover:text-rose-700"
                data-testid="push-notifications-disable-btn"
              >
                {disabling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BellOff className="h-4 w-4 mr-1.5" />}
                {disabling ? 'Turning off…' : 'Turn off'}
              </Button>
            </>
          )}
        </div>

        {permission === 'default' && !isSubscribed && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 text-xs text-slate-700 dark:text-slate-300">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />
            <div>
              When you click <strong>Turn on push alerts</strong>, your browser
              will show a tiny permission prompt — click <strong>Allow</strong>
              {' '}to finish setup.
            </div>
          </div>
        )}

        {lastResult?.ok && lastResult?.payload?.sent > 0 && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/60 text-xs text-emerald-900 dark:text-emerald-100"
            data-testid="push-notifications-last-result"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
            <div>
              Test push delivered to <strong>{lastResult.payload.sent}</strong>
              {' '}device(s).
              {lastResult.payload.removed_stale > 0 && (
                <span> Cleaned up {lastResult.payload.removed_stale} stale subscription(s).</span>
              )}
            </div>
          </div>
        )}

        {/* Phase 261k — Per-event-kind mute toggles, only relevant once push
            is actually enabled on this device. */}
        {isSubscribed && <PushPreferencesPanel />}
      </CardContent>
    </Card>
  );
}
